import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  BrregOrgDto,
  ContributingEntityRole,
  useAddContributingEntityMutation,
  type SelectionCriterionDto,
} from "../../../../../autogen/bff-api";
import { BrregSuggestionList } from "../../../../../common/brreg/BrregSuggestionList";
import { useBrregSearch } from "../../../../../common/brreg/useBrregSearch";
import { useApiError } from "../../../../../common/errors/useApiError";
import { getPreferredLanguage } from "../../../../../common/local-storage/language";
import { useBaseData } from "../../../../../common/useBaseData";
import { GroupRoleField, declaresGroupRole } from "./GroupRoleField";
import { SupportedCriteriaField } from "./SupportedCriteriaField";

interface FormValues {
  name: string;
  organizationNumber: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  groupRole: string;
  supportedCriterionKeys: string[];
}

interface Props {
  bidId: string;
  role: ContributingEntityRole;
  selectionCriteria: SelectionCriterionDto[];
  onClose: () => void;
}

export const AddContributingEntityModal = ({
  bidId,
  role,
  selectionCriteria,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const baseData = useBaseData();
  const norwayCountryId = baseData.countries.find((c) => c.alpha3Code === "NOR")?.id;

  const [addContributingEntity, { isLoading }] = useAddContributingEntityMutation();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
      organizationNumber: "",
      contactFirstName: "",
      contactLastName: "",
      contactEmail: "",
      groupRole: "",
      supportedCriterionKeys: [],
    },
  });

  const lendsCapacity = role === ContributingEntityRole.CapacityProvider;
  const needsGroupRole = declaresGroupRole(role);
  const supportedCriterionKeys = watch("supportedCriterionKeys") ?? [];

  const name = watch("name") ?? "";
  const [isNameFocused, setIsNameFocused] = useState(false);
  const lastSelectedNameRef = useRef<string | null>(null);
  const isAfterSelection = lastSelectedNameRef.current !== null && name === lastSelectedNameRef.current;

  const brregSearch = useBrregSearch({
    countryId: norwayCountryId,
    query: name,
    enabled: isNameFocused && !isAfterSelection,
  });

  const applyBrregOrg = (org: BrregOrgDto) => {
    lastSelectedNameRef.current = org.name;
    setValue("name", org.name, { shouldValidate: true });
    setValue("organizationNumber", org.organizationNumber, { shouldValidate: true });
    setIsNameFocused(false);
  };

  const submit = async (values: FormValues) => {
    try {
      await addContributingEntity({
        bidId,
        addContributingEntityRequestDto: {
          role,
          name: values.name.trim(),
          organizationNumber: values.organizationNumber.replace(/\s/g, ""),
          contactFirstName: values.contactFirstName.trim() || null,
          contactLastName: values.contactLastName.trim() || null,
          contactEmail: values.contactEmail.trim(),
          groupRole: needsGroupRole ? values.groupRole.trim() : null,
          language: getPreferredLanguage(),
          supportedCriterionKeys: lendsCapacity ? values.supportedCriterionKeys : [],
        },
      }).unwrap();
      onClose();
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const nameField = register("name", { required: t("Company name is required") ?? "" });
  const nameRef = useRef<HTMLInputElement | null>(null);
  register("supportedCriterionKeys", {
    validate: (value) =>
      !lendsCapacity ||
      selectionCriteria.length === 0 ||
      value.length > 0 ||
      (t("Select at least one qualification requirement") ?? ""),
  });

  return (
    <Modal isOpen={true} onClose={onClose} size="lg" initialFocusRef={nameRef}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Add participating company")}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(submit)}>
          <ModalBody>
            <Text fontSize="sm" color="smMuted" mb="4">
              {t(
                "The company receives an email invitation and answers its own self-declaration.",
              )}
            </Text>
            <Stack spacing="4">
              <FormControl isRequired isInvalid={errors.name !== undefined}>
                <FormLabel fontSize="sm">{t("Company name")}</FormLabel>
                <Box position="relative">
                  <Input
                    {...nameField}
                    ref={(el) => {
                      nameField.ref(el);
                      nameRef.current = el;
                    }}
                    autoComplete="off"
                    onFocus={() => setIsNameFocused(true)}
                    onBlur={(e) => {
                      nameField.onBlur(e);
                      window.setTimeout(() => setIsNameFocused(false), 150);
                    }}
                  />
                  {isNameFocused && <BrregSuggestionList result={brregSearch} onSelect={applyBrregOrg} />}
                </Box>
                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={errors.organizationNumber !== undefined}>
                <FormLabel fontSize="sm">{t("Organization number")}</FormLabel>
                <Input
                  {...register("organizationNumber", {
                    required: t("Organization number is required") ?? "",
                    validate: (value) =>
                      /^\d{9}$/.test(value.replace(/\s/g, "")) ||
                      (t("The organization number must have 9 digits.") ?? ""),
                  })}
                />
                <FormErrorMessage>{errors.organizationNumber?.message}</FormErrorMessage>
              </FormControl>

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="4">
                <FormControl>
                  <FormLabel fontSize="sm">{t("Contact first name")}</FormLabel>
                  <Input {...register("contactFirstName")} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t("Contact family name")}</FormLabel>
                  <Input {...register("contactLastName")} />
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired isInvalid={errors.contactEmail !== undefined}>
                <FormLabel fontSize="sm">{t("Contact email")}</FormLabel>
                <Input
                  type="email"
                  {...register("contactEmail", {
                    required: t("Contact email is required") ?? "",
                    validate: (value) => value.includes("@") || (t("Enter a valid email address") ?? ""),
                  })}
                />
                <FormErrorMessage>{errors.contactEmail?.message}</FormErrorMessage>
                <FormHelperText fontSize="xs">
                  {t("The contact person prefills the company's authorized representative.")}
                </FormHelperText>
              </FormControl>

              {needsGroupRole && (
                <GroupRoleField
                  role={role}
                  registration={register("groupRole", {
                    validate: (value) =>
                      !needsGroupRole || value.trim().length > 0 || (t("This field is required") ?? ""),
                  })}
                  error={errors.groupRole?.message}
                />
              )}

              {lendsCapacity && (
                <SupportedCriteriaField
                  selectionCriteria={selectionCriteria}
                  value={supportedCriterionKeys}
                  onChange={(next) => setValue("supportedCriterionKeys", next, { shouldValidate: true })}
                  error={errors.supportedCriterionKeys?.message}
                />
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button type="submit" colorScheme="teal" isLoading={isLoading}>
              {t("Add and send invitation")}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
